import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatCDF } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import AiInsightCard from "../../components/AiInsightCard";
import { Badge, Card, StatusBadge } from "../../components/ui-kit";
import { Brain, Warning, ShieldCheck, UsersThree, CarProfile, CreditCard, Megaphone, SlidersHorizontal, MapPin, FileText, RocketLaunch, Storefront, Database } from "@phosphor-icons/react";

const CONTROL_CENTER = [
  { to: "/admin/users", label: "Utilisateurs", desc: "Roles, blocage, verification, reset mot de passe", icon: UsersThree },
  { to: "/admin/drivers", label: "Chauffeurs", desc: "Validation, documents, vehicules et statut", icon: CarProfile },
  { to: "/admin/rides", label: "Courses", desc: "Demandes, matching, annulations et suivi", icon: CarProfile },
  { to: "/admin/rides", label: "Livraisons", desc: "Colis, livreurs, destinataires et paiements directs", icon: RocketLaunch },
  { to: "/admin/marketplace", label: "Marketplace", desc: "Restaurants, supermarches, commandes et agents terrain", icon: Storefront },
  { to: "/admin/platform", label: "Plateforme", desc: "Tous les modules Uber-like, statuts et prochaines etapes", icon: Database },
  { to: "/admin/finance", label: "Finance", desc: "Commissions, revenus, retraits et export", icon: CreditCard },
  { to: "/admin/pricing", label: "Tarifs", desc: "Prix par ville, type vehicule, frais et promos", icon: SlidersHorizontal },
  { to: "/admin/overview", label: "Villes et zones", desc: "Rayons service, villes actives et zones", icon: MapPin },
  { to: "/admin/ads", label: "Publicites", desc: "Campagnes, promos, affiches IA et approbation", icon: Megaphone },
  { to: "/admin/events", label: "Events / shuttle", desc: "Evenements, navettes, tickets et capacites", icon: RocketLaunch },
  { to: "/admin/documents", label: "Documents", desc: "Pieces, OCR IA, validation et archives", icon: FileText },
  { to: "/admin/launch", label: "Lancement", desc: "PWA, push, secrets, monitoring et sauvegardes", icon: RocketLaunch },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [aiOps, setAiOps] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/rides"),
        ]);
        setStats(s.data);
        setRides(r.data);
        api.get("/admin/ai/ops-insights")
          .then(({ data }) => setAiOps(data))
          .catch(() => {});
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
    const int = setInterval(load, 5000);
    return () => clearInterval(int);
  }, []);

  const kpis = stats
    ? [
        { label: "Clients", v: stats.total_customers },
        { label: "Chauffeurs", v: stats.total_drivers },
        { label: "En attente", v: stats.pending_drivers, tone: "warning" },
        { label: "Courses totales", v: stats.total_rides },
        { label: "Livraisons", v: stats.total_deliveries || 0, tone: "primary" },
        { label: "Partenaires", v: stats.total_merchants || 0, tone: "primary" },
        { label: "Commandes", v: stats.total_merchant_orders || 0, tone: "warning" },
        { label: "Agents", v: stats.total_agents || 0, tone: "success" },
        { label: "Courses actives", v: stats.active_rides, tone: "success" },
        { label: "Livraisons actives", v: stats.active_deliveries || 0, tone: "success" },
        { label: "Courses terminées", v: stats.completed_rides },
        { label: "Revenu total", v: formatCDF(stats.total_revenue), wide: true, primary: true },
      ]
    : [];

  return (
    <AdminShell title="Dashboard">
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Vue d'ensemble</h1>
      <Card className="mt-6 p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-primary">Centre de controle admin</div>
            <h2 className="font-heading text-2xl font-black tracking-tight">Tout modifier, voir et piloter</h2>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Gestion complete des utilisateurs, chauffeurs, courses, finances, villes, zones, publicites, documents et securite production.
            </p>
          </div>
          <Badge tone="success">Admin complet</Badge>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {CONTROL_CENTER.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="group flex min-h-[5.75rem] items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 transition hover:border-primary/40 hover:bg-primary/10"
                data-testid={`admin-control-${item.label.toLowerCase().replaceAll(" ", "-")}`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon size={22} weight="duotone" />
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-black">{item.label}</span>
                  <span className="mt-1 block text-[11px] leading-4 text-muted-foreground">{item.desc}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </Card>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.label}
            className={`p-4 ${k.primary ? "bg-primary text-primary-foreground" : ""} ${k.wide ? "sm:col-span-2" : ""}`}
            data-testid={`kpi-${k.label}`}
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              {k.label}
            </div>
            <div className="font-heading text-3xl font-black tracking-tighter">{k.v}</div>
          </Card>
        ))}
      </div>

      <AiInsightCard title="Assistant IA admin" limit={3} className="mt-8" />

      <Card className="mt-8 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-foreground p-4">
          <div className="flex items-center gap-2">
            <div className="rounded-xl bg-primary/15 p-2 text-primary">
              <Brain size={22} weight="duotone" />
            </div>
            <div>
              <div className="font-heading text-xl font-black tracking-tight">AI Ops</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Detection automatique des fails
              </div>
            </div>
          </div>
          {aiOps && (
            <div className="flex flex-wrap gap-2">
              {aiOps.summary.critical > 0 && <Badge tone="danger">{aiOps.summary.critical} critique</Badge>}
              {aiOps.summary.high > 0 && <Badge tone="warning">{aiOps.summary.high} haut</Badge>}
              {aiOps.summary.medium > 0 && <Badge tone="primary">{aiOps.summary.medium} moyen</Badge>}
              {aiOps.summary.ok > 0 && <Badge tone="success">Stable</Badge>}
            </div>
          )}
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-2">
          {!aiOps && (
            <div className="rounded-2xl bg-white/5 p-4 text-sm text-muted-foreground">
              Analyse en cours...
            </div>
          )}
          {aiOps?.insights?.map((item) => {
            const tone = {
              critical: "danger",
              high: "warning",
              medium: "primary",
              low: "default",
              ok: "success",
            }[item.severity] || "default";
            const Icon = item.severity === "ok" ? ShieldCheck : Warning;
            return (
              <div key={item.id} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`ai-${item.category}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Icon size={20} weight="fill" className={item.severity === "ok" ? "text-secondary" : "text-warning"} />
                    <div>
                      <div className="text-sm font-black">{item.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{item.message}</div>
                    </div>
                  </div>
                  <Badge tone={tone}>{item.severity}</Badge>
                </div>
                <div className="mt-3 rounded-xl bg-background/40 p-3 text-xs">
                  <span className="font-bold text-primary">Action: </span>
                  <span className="text-muted-foreground">{item.action}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="mt-8">
        <div className="border-b-2 border-foreground p-4 font-heading text-xl font-black tracking-tight">
          Courses récentes
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b-2 border-foreground bg-secondary text-secondary-foreground">
              <tr>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">ID</th>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">Client</th>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">Chauffeur</th>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">Type</th>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">Prix</th>
                <th className="p-3 text-[10px] font-black uppercase tracking-widest">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rides.slice(0, 10).map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="p-3 font-mono text-xs">#{r.id.slice(0, 6).toUpperCase()}</td>
                  <td className="p-3">{r.customer_name}</td>
                  <td className="p-3">{r.driver_name || "—"}</td>
                  <td className="p-3">{r.vehicle_type}</td>
                  <td className="p-3 font-mono font-bold">{formatCDF(r.price)}</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {rides.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Aucune course enregistrée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminShell>
  );
}
